import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { AccessGrant } from '@photos/shared';
import { useAccessAlbums, useAccessGrants } from '../../api/hooks';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { Who } from '../../components/Who';
import { fmtShortDate } from '../../lib/format';
import { useSession } from '../../state/session';
import { AddPeople, RemoveAccessDialog } from './AlbumPeople';

/** Pick an album, then search people and add them in one click. */
function AddAccessDialog({ onClose }: { onClose(): void }) {
  const albums = useAccessAlbums();
  const [albumId, setAlbumId] = useState('');
  const album = albums.data?.items.find((a) => a.id === albumId);
  return (
    <Modal onClose={onClose} labelledBy="add-access-title">
      <h3 id="add-access-title">Add album access</h3>
      <p className="help">The person can then upload and tag in that album only. Media already have every album.</p>
      <div className="field">
        <select className="inp" aria-label="Album" value={albumId} onChange={(e) => setAlbumId(e.target.value)}>
          <option value="">Choose an album…</option>
          {(albums.data?.items ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>
      {album && <AddPeople key={album.id} albumId={album.id} albumName={album.name} />}
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}

/** `/studio/access`: every album grant (invite link or added directly), with filters, remove and add. Media and admins. */
export function AccessPage() {
  const { access } = useSession();
  const grants = useAccessGrants({});
  const [q, setQ] = useState('');
  const [person, setPerson] = useState('');
  const [albumId, setAlbumId] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<AccessGrant | null>(null);
  const all = useMemo(() => grants.data?.items ?? [], [grants.data]);
  const people = useMemo(() => [...new Map(all.map((g) => [g.userId, g.userName])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [all]);
  const albums = useMemo(() => [...new Map(all.map((g) => [g.albumId, g.albumName])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [all]);
  const term = q.trim().toLowerCase();
  const rows = all.filter(
    (g) =>
      (!person || g.userId === person) &&
      (!albumId || g.albumId === albumId) &&
      (!term || [g.userName, g.userEmail, g.albumName, g.grantedByName ?? ''].some((f) => f.toLowerCase().includes(term))),
  );

  return (
    <>
      <div className="toolbar">
        <h1>Album access</h1>
        <span className="meta">People who can upload and tag in specific albums (through an invite link, or added directly). Media have every album.</span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input className="inp sm search" type="search" placeholder="Search people or albums" aria-label="Search album access" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="inp sm" aria-label="Person" value={person} onChange={(e) => setPerson(e.target.value)} style={{ width: 'auto' }}>
          <option value="">Everyone</option>
          {people.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <select className="inp sm" aria-label="Album" value={albumId} onChange={(e) => setAlbumId(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All albums</option>
          {albums.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <button type="button" className="btn sm primary" onClick={() => setAdding(true)}>
          <Icon name="plus" />
          Add access
        </button>
      </div>
      {grants.data && !rows.length ? (
        <div className="empty">
          <h3>{all.length ? 'No matches' : 'No album access yet'}</h3>
          {all.length ? 'Try another search or filter.' : 'Give someone an album with Add access, or share an invite link from an album’s People panel.'}
        </div>
      ) : (
        <div className="tscroll">
          <table className="t">
            <thead>
              <tr>
                <th>Person</th>
                <th>Album</th>
                <th>Granted by</th>
                <th>Granted</th>
                <th>How</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={`${g.albumId}/${g.userId}`}>
                  <td>
                    <Who name={g.userName} sub={g.userEmail} />
                  </td>
                  <td>{access.canOpenAlbum(g.albumId) ? <Link to={`/studio/albums/${encodeURIComponent(g.albumId)}`}>{g.albumName}</Link> : g.albumName}</td>
                  <td>{g.grantedByName ?? '—'}</td>
                  <td style={{ color: 'var(--muted)' }}>{fmtShortDate(new Date(g.grantedAt))}</td>
                  <td>{g.how === 'invite' ? 'Invite link' : 'Added directly'}</td>
                  <td className="row-actions">
                    <button type="button" className="btn sm ghost" aria-label={`Remove ${g.userName} from ${g.albumName}`} onClick={() => setRemoving(g)}>
                      <Icon name="x" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && <AddAccessDialog onClose={() => setAdding(false)} />}
      {removing && <RemoveAccessDialog grant={removing} onClose={() => setRemoving(null)} />}
    </>
  );
}
